import * as React from "react";

import { cn } from "utils/cn";

type Environment = "production" | "staging" | "development";

const LABELS: Record<Environment, string> = {
  production: "Production",
  staging: "Staging",
  development: "Development",
};

/**
 * Which SAP deployment this is. Set REACT_APP_SAP_ENV at build time (production | staging |
 * development); local dev servers default to development, other builds without it show nothing.
 */
export const resolveEnvironment = (): Environment | null => {
  const value = (process.env.REACT_APP_SAP_ENV || "").trim().toLowerCase();
  if (value === "production" || value === "prod") return "production";
  if (value === "staging" || value === "stage" || value === "uat") return "staging";
  if (value === "development" || value === "dev" || value === "local") return "development";
  return process.env.NODE_ENV === "development" ? "development" : null;
};

export interface EnvBadgeProps {
  className?: string;
  /** Styles for a dark background (auth brand panel). */
  onDark?: boolean;
}

export const EnvBadge: React.FC<EnvBadgeProps> = ({ className, onDark = false }) => {
  const env = resolveEnvironment();
  if (!env) {
    return null;
  }
  const isProduction = env === "production";
  return (
    <span
      data-ui="env-badge"
      title={`You are signed in to the ${LABELS[env].toLowerCase()} SAP`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider",
        isProduction
          ? onDark
            ? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
            : "border-warning/25 bg-warning-subtle text-warning-text"
          : onDark
          ? "border-white/15 bg-white/5 text-white/70"
          : "border-border bg-surface-muted text-muted-foreground",
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          isProduction
            ? onDark
              ? "bg-brand-warning"
              : "bg-warning"
            : onDark
            ? "bg-white/60"
            : "bg-subtle-foreground"
        )}
      />
      {LABELS[env]}
    </span>
  );
};
